import { toast } from 'react-toastify';
import type { AppDispatch } from '@/app/store/store';
import {
    setQuoteId,
    setQuoteVersion,
    setQuoteRef,
    setStatus,
    setSelectedDealer,
    setSelectedVehicle,
    setSelectedProduct,
    setConfiguredServices,
    setConfiguredAddOns,
    requestAutoAddOnBaseline,
    setPaymentOptions,
    setPaymentSelection,
    setSelectedCustomer,
    setCurrentStep,
    setResolvedPlanId,
    setServicePlanSessionId,
    setSmriqConfirmedVehicle,
    setSmriqPlanSettings,
    setScheduleOverrides,
    resetFromStep,
} from './quoteSlice';
import { isValidPaymentSelection } from '../types/schedule.types';
import type { QuoteStep, QuoteSummary } from '../types/quote.types';
import { landingStep, resumeStepFor, type QuoteLanding } from '../utils/resumeStep';
import {
    verifyScheduleOverrides,
    clampScheduleOverrideRows,
} from '@features/service-plans/utils/scheduleOverridesGuard';
import { SCHEDULE_OVERRIDE_MESSAGES } from '@features/service-plans/utils/scheduleOverrideMessages';
import { derivationFingerprint } from '@features/service-plans/utils/derivationFingerprint';
import { earliestPricedDueDate, PAST_SERVICE_DATE_WARNING } from '../services/pastDueScheduling';
import { deriveVehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';
import { todayDateKey } from '../utils/dateKey';
import { normaliseLegacyFeeVat } from '../utils/legacyFeeVat';
import { hasDealerSegment } from '../utils/quoteRefDealerSegment';

interface HydrateOptions {
    order: readonly QuoteStep[];
    defer: boolean;
    landing: QuoteLanding;
    announce: boolean;
}

const toastOptions = { position: 'bottom-right' } as const;

interface OverrideOutcome {
    overridesDiscarded: boolean;
    dispatchOverrides: () => void;
}

const planScheduleOverrides = (dispatch: AppDispatch, quote: QuoteSummary, serviceCount: number): OverrideOutcome => {
    const clamped = quote.scheduleOverrides
        ? clampScheduleOverrideRows(quote.scheduleOverrides, serviceCount)
        : null;
    const verdict = clamped
        ? verifyScheduleOverrides(
              clamped,
              derivationFingerprint({
                  productId: quote.product?.id ?? null,
                  resolvedPlanId: quote.resolvedPlanId ?? null,
                  vehicleContext: deriveVehicleScheduleContext(quote.vehicle),
              }),
              todayDateKey(),
          )
        : null;

    const dispatchOverrides = (): void => {
        if (!clamped || !verdict) {
            dispatch(setScheduleOverrides(undefined));
        } else if (verdict.usable) {
            dispatch(setScheduleOverrides(clamped));
        } else if (verdict.scope === 'dates') {
            dispatch(setScheduleOverrides({ ...clamped, dateOverrides: {} }));
            toast.error(SCHEDULE_OVERRIDE_MESSAGES.expired, toastOptions);
        } else {
            dispatch(setScheduleOverrides(undefined));
            toast.error(SCHEDULE_OVERRIDE_MESSAGES[verdict.reason], toastOptions);
        }
    };
    return { overridesDiscarded: verdict !== null && !verdict.usable, dispatchOverrides };
};

export const hydrateLoadedQuote = (
    dispatch: AppDispatch,
    quote: QuoteSummary,
    { order, defer, landing, announce }: HydrateOptions,
): { landedOn: QuoteStep } => {
    const services = Array.isArray(quote.services) ? quote.services : [];
    const addOns = Array.isArray(quote.addOns) ? quote.addOns : [];

    if (quote.id) dispatch(setQuoteId(quote.id));
    dispatch(setQuoteVersion(quote.version));
    if (quote.quote_ref) dispatch(setQuoteRef(quote.quote_ref));
    if (quote.status) dispatch(setStatus(quote.status));
    dispatch(setSelectedVehicle(quote.vehicle));
    dispatch(setSelectedProduct(normaliseLegacyFeeVat(quote.product)));
    dispatch(setConfiguredServices(services));
    dispatch(setConfiguredAddOns(addOns));
    dispatch(requestAutoAddOnBaseline());
    dispatch(resetFromStep({ step: 'payment', order }));
    dispatch(setPaymentOptions(quote.payment));
    const restoredDealer = defer && !hasDealerSegment(quote.quote_ref) ? null : quote.dealer;
    dispatch(setSelectedDealer(restoredDealer));

    const { overridesDiscarded, dispatchOverrides } = planScheduleOverrides(dispatch, quote, services.length);
    if (!overridesDiscarded && isValidPaymentSelection(quote.paymentSelection)) {
        dispatch(setPaymentSelection(quote.paymentSelection));
    }
    dispatch(setSelectedCustomer(quote.customer || null));
    dispatch(setResolvedPlanId(quote.resolvedPlanId ?? null));
    dispatch(setServicePlanSessionId(quote.servicePlanSessionId ?? null));
    dispatch(setSmriqConfirmedVehicle(quote.smriqConfirmedVehicle ?? null));
    dispatch(setSmriqPlanSettings(quote.smriqPlanSettings ?? null));
    dispatchOverrides();

    const earliestDue = earliestPricedDueDate(services, addOns);
    if (earliestDue !== null && earliestDue < todayDateKey()) {
        toast.error(PAST_SERVICE_DATE_WARNING, toastOptions);
    }

    const resume = resumeStepFor(quote, { order, restoredDealer, overridesDiscarded });
    const landedOn = landingStep(order, landing, resume, quote.status);
    if (landing.kind === 'resume') {
        dispatch(setCurrentStep('summary'));
        if (resume !== 'summary') dispatch(setCurrentStep(resume));
    } else {
        dispatch(setCurrentStep(landedOn));
    }

    // eslint-disable-next-line no-console -- identifiers only; never log the quote, customer or vehicle
    console.log('Loaded existing quote into Redux store:', {
        quoteId: quote.id,
        quoteRef: quote.quote_ref,
        status: quote.status,
        resumeStep: landedOn,
    });

    if (announce) toast.success(`Quote ${quote.quote_ref} loaded successfully`, toastOptions);
    return { landedOn };
};
